import HelpIcon from "@mui/icons-material/Help";
import {
    Backdrop,
    Button,
    CircularProgress,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Slider,
    TextareaAutosize,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import GenericAlert from "common/components/GenericAlert/GenericAlert";
import GenericDialog from "common/components/GenericDialog/GenericDialog";
import { ApiService } from "common/services/ApiService";
import { UseAlert } from "hooks/UseAlert";
import SocialNetworkChart from "modules/activity/pages/Activities/components/SocialNetworkChart";
import SocialNetworkCommentsChart from "modules/activity/pages/Activities/components/SocialNetworkCommentsChart";
import { useState } from "react";
import { exportGraphToImg, exportMultipleChartsToPdf } from "utils/exportPdf";
import ExcludedWords from "./components/ExcludedWords";
import { useParams } from "react-router-dom";

const API_GRAPHER = {
    social_word_frequency: ApiService.getRedditGraph,
    social_user_comments: ApiService.getRedditGraphUserComments,
};

const SocialGraphs = ({
    dataGraphCached,
    setDataGraphCached,
    graphSelectedMap,
    setGraphSelectedMap,
    sendResolution,
    studentActivity = null,
    commentResolution,
    setOpenResolutionDialog,
    activity,
}) => {
    const { idActivity } = useParams();
    const mapAlertSuccess = {
        anotherSearch: "Gráfico removido",
    };
    const [openLoader, setOpenLoader] = useState(false);
    const [graph, setGraph] = useState("social_user_comments");
    const [textValue, setTextValue] = useState("");
    const [textValueError, setTextValueError] = useState(false);
    const [disabledMode, setDisabledMode] = useState(false);
    const [openRemoveGraph, setOpenRemoveGraph] = useState(false);
    const [openPartialDeliveryDialog, setOpenPartialDeliveryDialog] = useState(false);
    const [openAddToDelivery, setOpenAddToDelivery] = useState(false);
    const [openDialogActualComment, setOpenDialogActualComment] = useState(false);
    const [commentEditActualGraph, setCommentEditActualGraph] = useState("");
    const [confirmEditComment, setConfirmEditComment] = useState(false);

    const initialStateItems = [
        { name: "Preposiciones", checked: false, value: "prepositions" },
        { name: "Artículos", checked: false, value: "articles" },
        { name: "Pronombres", checked: false, value: "pronouns" },
        {
            name: "Conjunciones de ser, estar, haber y tener",
            checked: false,
            value: "to_be_and_more",
        },
        { name: "Adverbios", checked: false, value: "adverbs" },
        { name: "Conjugaciones", checked: false, value: "conjunctions" },
    ];
    const [sliderValue, setSliderValue] = useState(5);
    const [itemsState, setItemsState] = useState(initialStateItems);

    const {
        alertText,
        showAlert,
        confirmAction,
        executeAction,
        openDialog,
        setOpenDialog,
        dialogContentText,
        severity,
        setAlertText,
        setSeverity,
        setShowAlert,
        handleShowAlert,
    } = UseAlert(mapAlertSuccess);

    const handleChange = (event, selectedItem) => {
        const newState = itemsState.map((item) => {
            if (item.name === selectedItem.name) {
                return { ...item, checked: event.target.checked };
            }
            return item;
        });

        setItemsState(newState);
    };

    async function getData() {
        setOpenLoader(true);

        try {
            const cleanedTextValue = textValue.replace(/\s+/g, "");

            const params = {
                word: cleanedTextValue, // Usamos el texto sin espacios
                frequency: sliderValue,
                exclude_list: itemsState
                    .filter((i) => i.checked)
                    .map((i) => i.value)
                    .join("|"),
            };

            const response = await API_GRAPHER[graph](params);

            if (response.data.links) {
                setDataGraphCached({
                    ...dataGraphCached,
                    [graph]: response.data,
                });
                setGraphSelectedMap({
                    ...graphSelectedMap,
                    [graph]: {
                        ...graphSelectedMap[graph],
                        nodesId: response.data?.nodes_id,
                    },
                });
            } else {
                setDisabledMode(false);
                handleShowAlert("error", `No se encontraron resultados para: ${textValue}`);
            }
        } catch (error) {
            setAlertText("Ocurrió un error intente nuevamente!");
            setSeverity("error");
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
                setAlertText("");
                setSeverity("");
            }, 3000);
        } finally {
            setOpenLoader(false);
        }
    }

    const handleSearch = () => {
        if (!textValue.trim()) {
            setTextValueError(true);

            return;
        }
        setDisabledMode(true);
        getData();
    };

    const anotherSearch = () => {
        setDataGraphCached({
            ...dataGraphCached,
            [graph]: {},
        });
        setTextValue("");
        setSliderValue(5);
        setItemsState(initialStateItems);
        setDisabledMode(!disabledMode);
        setGraphSelectedMap({
            ...graphSelectedMap,
            [graph]: {
                selected: false,
                export: { image: null, offsetHeight: null, offsetWidth: null },
                filterInclude: [],
                filterExclude: [],
                dataSource: "dynamic",
                nodesId: [],
            },
        });
    };

    const onClickAddPartialDelivery = () => {
        const selected = graphSelectedMap[graph].selected;
        if (selected) {
            setOpenRemoveGraph(true);
            return;
        }
        setOpenPartialDeliveryDialog(true);
    };

    const addPartialDelivery = async () => {
        try {
            setOpenLoader(false);
            setOpenPartialDeliveryDialog(false);
            const exportData = {};
            const selected = graphSelectedMap[graph].selected;
            if (!selected) {
                const el = document.getElementById("chart-component");
                exportData.offsetHeight = el.offsetHeight;
                exportData.offsetWidth = el.offsetWidth;
                exportData.image = await exportGraphToImg(el);
            }

            let filterExclude = [];
            let filterInclude = [];

            if (graph === "social_word_frequency") {
                filterExclude = itemsState
                    .filter((i) => i.checked)
                    .map((i) => ({ value: i.value, label: i.name }));
                filterInclude = [
                    {
                        value: "cant_words",
                        label: `Cantidad Palabras mayor a ${sliderValue}`,
                    },
                ];
            }

            setGraphSelectedMap({
                ...graphSelectedMap,
                [graph]: {
                    ...graphSelectedMap[graph],
                    selected: !selected,
                    export: exportData,
                    frequency: sliderValue,
                    word: textValue,
                    filterExclude,
                    filterInclude,
                },
            });
            setOpenLoader(false);
        } catch (error) {
            console.error("Error exportando", error);
            setOpenLoader(false);
        }
    };

    return (
        <div>
            <Grid container justifyContent="center">
                <Grid item xs={12} md={6}>
                    <TextField
                        disabled={disabledMode}
                        value={textValue}
                        error={textValueError}
                        label="Subreddit"
                        placeholder="Ingrese subreddit sin r/ y sin espacios"
                        variant="outlined"
                        fullWidth
                        helperText={
                            <>
                                Este buscador realiza la misma búsqueda que puedes hacer
                                directamente en{" "}
                                <a
                                    href="https://www.reddit.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    https://www.reddit.com/
                                </a>
                                . Los subreddits obtenidos se utilizarán como entrada de datos para
                                los grafos. Los subreddits comienzan con 'r/'. Ejemplo:
                                'technology', 'gaming'. Los subreddits en inglés suelen tener más
                                contenido, por lo que los de otros idiomas podrían tener menos
                                información en el grafo. Los nombres no deben tener espacios.
                                <br />
                                <strong>Nota:</strong> La búsqueda devuelve los resultados más
                                relevantes.
                            </>
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onChange={(evt) => {
                            setTextValue(evt.target.value);
                            setTextValueError(false);
                        }}
                    />

                    {textValueError === true ? (
                        <Typography variant="p" fontSize={13} ml={0.2} color={"red"}>
                            El campo es obligatorio
                        </Typography>
                    ) : (
                        ""
                    )}
                    <br />
                    <br />
                    <FormControl disabled={disabledMode}>
                        <FormLabel id="visilibty">Seleccione tipo de grafo</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={graph}
                            onChange={(e) => setGraph(e.target.value)}
                        >
                            <FormControlLabel
                                value="social_user_comments"
                                control={<Radio />}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Tooltip
                                            enterTouchDelay={0}
                                            leaveTouchDelay={15000}
                                            disableTouchListener={false}
                                            title={
                                                <img
                                                    src={`/img/charts/grafo-usuarios.jpeg`}
                                                    alt="Grafo"
                                                    width="100%"
                                                    // height="20"
                                                />
                                            }
                                        >
                                            <p>Grafo de interacción entre usuarios</p>
                                        </Tooltip>
                                        <Tooltip
                                            enterTouchDelay={0}
                                            leaveTouchDelay={15000}
                                            disableTouchListener={false}
                                            title={
                                                <Typography variant="body2">
                                                    Este grafo representa la interacción entre
                                                    usuarios en un subreddit sobre el primer post de
                                                    los más relevantes.
                                                    <br />
                                                    <br />
                                                    Se analizan los comentarios de las
                                                    publicaciones, identificando quién responde a
                                                    quién.
                                                    <br />
                                                    <br />
                                                    Cada nodo es un usuario, y los enlaces muestran
                                                    respuestas entre ellos.
                                                    <br />
                                                    <br />
                                                    Se exploran hasta 200 conexiones dentro del
                                                    post.
                                                </Typography>
                                            }
                                        >
                                            <IconButton size="small">
                                                <HelpIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                }
                            />

                            <FormControlLabel
                                value="social_word_frequency"
                                control={<Radio />}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Tooltip
                                            enterTouchDelay={0}
                                            leaveTouchDelay={15000}
                                            disableTouchListener={false}
                                            title={
                                                <img
                                                    src={`/img/charts/grafo-ocurrencias.jpeg`}
                                                    alt="Grafo"
                                                    width="100%"
                                                    // height="20"
                                                />
                                            }
                                        >
                                            <p>Grafo de coocurrencias de palabras</p>
                                        </Tooltip>
                                        <Tooltip
                                            enterTouchDelay={0}
                                            leaveTouchDelay={15000}
                                            disableTouchListener={false}
                                            title={
                                                <Typography variant="body2">
                                                    Este grafo analiza los títulos de hasta 100
                                                    publicaciones en un subreddit.
                                                    <br />
                                                    <br />
                                                    Luego, crea conexiones entre las palabras de
                                                    cada título representándose como un nodo.
                                                </Typography>
                                            }
                                        >
                                            <IconButton size="small">
                                                <HelpIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    {graph === "social_word_frequency" && (
                        <div>
                            <Grid container alignItems={"center"} justifyContent={"space-between"}>
                                <Grid item display={"flex"} justifyContent={"center"}>
                                    <h4>Apariciones mínimas</h4>
                                    <Tooltip
                                        enterTouchDelay={0}
                                        leaveTouchDelay={15000}
                                        disableTouchListener={false}
                                        title={
                                            <Typography variant="body2">
                                                Indica la cantidad mínima de veces que se repite una
                                                palabra para ser considerada en el grafo de
                                                respuesta. Estas palabras se obtienen analizando los
                                                títulos de las publicaciones en el subreddt elegido,
                                                tomándolas por orden de popularidad. Este valor se
                                                puede ajustar para que la cantidad de veces sea
                                                desde mayor o igual a 1 hasta, mayor o igual a 10
                                                apariciones.
                                            </Typography>
                                        }
                                    >
                                        <IconButton size="small">
                                            <HelpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item md={6}>
                                    <Slider
                                        disabled={disabledMode}
                                        aria-label="Apariciones mínimas"
                                        defaultValue={5}
                                        valueLabelDisplay="auto"
                                        value={sliderValue}
                                        step={1}
                                        marks
                                        min={1}
                                        max={10}
                                        onChange={(evt) => setSliderValue(evt.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <ExcludedWords
                                    itemsState={itemsState}
                                    handleChange={handleChange}
                                    disabledMode={disabledMode}
                                ></ExcludedWords>
                            </Grid>
                        </div>
                    )}
                    <Grid container pt={2} pb={2}>
                        <Grid item xs={6} textAlign={"center"}>
                            <Button
                                variant="contained"
                                disabled={disabledMode || !textValue}
                                onClick={handleSearch}
                            >
                                Buscar
                            </Button>
                        </Grid>
                        <Grid item xs={6} textAlign={"center"}>
                            <Button
                                variant="contained"
                                disabled={!disabledMode || !textValue}
                                onClick={() => {
                                    confirmAction(
                                        "anotherSearch",
                                        () => anotherSearch(),
                                        "Al realizar otra búsqueda se perderá el progreso actual ¿Desea continuar?"
                                    );
                                }}
                            >
                                Realizar otra busqueda
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {graph === "social_word_frequency" && (
                <Grid container display={"flex"} justifyContent={"center"}>
                    <Grid item xs={12} md={9}>
                        <SocialNetworkChart data={dataGraphCached[graph]} />
                    </Grid>
                </Grid>
            )}

            {graph === "social_user_comments" && (
                <Grid container display={"flex"} justifyContent={"center"}>
                    <Grid item xs={12} md={9}>
                        <SocialNetworkCommentsChart data={dataGraphCached[graph]} />
                    </Grid>
                </Grid>
            )}

            {!Boolean(idActivity) && (
                <Grid container px={10} py={4} gap={1} justifyContent={"space-between"}>
                    <Button
                        disabled={Object.keys(dataGraphCached[graph]).length === 0}
                        variant="contained"
                        onClick={() => onClickAddPartialDelivery()}
                        sx={{
                            backgroundColor: graphSelectedMap[graph].selected
                                ? "#db0909"
                                : "#12416F",
                            "&:hover": {
                                backgroundColor: graphSelectedMap[graph].selected
                                    ? "#db0909"
                                    : "#12416F",
                            },
                        }}
                    >
                        {graphSelectedMap[graph].selected
                            ? "Remover de entrega"
                            : "Agregar a entrega"}
                    </Button>
                    {graphSelectedMap[graph].selected && graphSelectedMap[graph]?.comment && (
                        <Button
                            disabled={Object.keys(dataGraphCached[graph]).length === 0}
                            variant="contained"
                            onClick={() => {
                                setCommentEditActualGraph(graphSelectedMap[graph]?.comment);
                                setOpenDialogActualComment(!openDialogActualComment);
                            }}
                        >
                            Editar observaciones
                        </Button>
                    )}
                    <Button
                        disabled={
                            Object.keys(dataGraphCached[graph]).length === 0 ||
                            (Object.keys(dataGraphCached[graph]).length > 0 &&
                                !graphSelectedMap[graph].selected)
                        }
                        variant="contained"
                        onClick={() => {
                            exportMultipleChartsToPdf(
                                Object.entries(graphSelectedMap)
                                    .filter(([key, value]) => value.selected)
                                    .map(([key, value]) => ({
                                        ...value.export,
                                        filterExclude: value.filterExclude,
                                        filterInclude: value.filterInclude,
                                        graph: key,
                                        comment: value.comment,
                                    })),
                                [],
                                studentActivity,
                                activity,
                                commentResolution || ""
                            );
                        }}
                    >
                        Previsualizar
                    </Button>
                    <Button
                        disabled={
                            Object.keys(dataGraphCached[graph]).length === 0 ||
                            (Object.keys(dataGraphCached[graph]).length > 0 &&
                                !graphSelectedMap[graph].selected)
                        }
                        variant="contained"
                        onClick={() => setOpenResolutionDialog(true)}
                    >
                        Realizar Entrega
                    </Button>
                </Grid>
            )}
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: 999999999,
                }}
                open={openLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <GenericDialog
                title="Confirmación"
                open={openDialog}
                setOpen={setOpenDialog}
                handleSave={executeAction}
                saveButtonText="Sí"
                mandatoryText={false}
            >
                <DialogContent>{dialogContentText}</DialogContent>
            </GenericDialog>
            <GenericDialog
                title="¿Está seguro de que desea agregar el gráfico y las observaciones como parte de la entrega?"
                open={openAddToDelivery}
                setOpen={setOpenAddToDelivery}
                handleSave={() => {
                    setOpenLoader(true);
                    setTimeout(() => {
                        addPartialDelivery();
                        setOpenAddToDelivery(!openAddToDelivery);
                    }, 2000);
                }}
                cancelButtonText="No"
                saveButtonText="Si"
                mandatoryText={false}
            ></GenericDialog>
            <GenericDialog
                title="¿Desea remover de la entrega al gráfico y a sus observaciones?"
                open={openRemoveGraph}
                setOpen={setOpenRemoveGraph}
                handleSave={() => {
                    setGraphSelectedMap({
                        ...graphSelectedMap,
                        [graph]: {
                            ...graphSelectedMap[graph],
                            selected: !graphSelectedMap[graph].selected,
                            comment: "",
                            export: {},
                        },
                    });
                    setOpenRemoveGraph(false);
                }}
                cancelButtonText="No"
                saveButtonText="Si"
                mandatoryText={false}
            ></GenericDialog>

            <GenericDialog
                title="Agregar a entrega"
                open={openPartialDeliveryDialog}
                setOpen={setOpenPartialDeliveryDialog}
                handleSave={() => {
                    if ((graphSelectedMap[graph]?.comment?.length || 0) === 0) {
                        setAlertText("Debe completar la observación!");
                        setSeverity("error");
                        setShowAlert(true);
                        setTimeout(() => {
                            setShowAlert(false);
                            setAlertText("");
                            setSeverity("");
                        }, 2000);
                        return;
                    }
                    setOpenAddToDelivery(!openAddToDelivery);
                }}
                onClose={() => {
                    setGraphSelectedMap({
                        ...graphSelectedMap,
                        [graph]: {
                            ...graphSelectedMap[graph],
                            comment: "",
                        },
                    });
                }}
            >
                <DialogContent>
                    <FormControl>
                        <h3 style={{ margin: 0 }}>Actividad</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.title}
                        </Typography>
                        <h3 style={{ margin: 0 }}>Enunciado</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.statement}
                        </Typography>
                        <h3 style={{ margin: 0 }}>Descripción</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.description}
                        </Typography>
                        <h3>
                            A partir del gráfico analizado, ¿Qué observaciones puede compartir con
                            el docente?*
                        </h3>
                        <TextareaAutosize
                            maxLength={700}
                            value={graphSelectedMap[graph]?.comment}
                            onChange={(evt) => {
                                setGraphSelectedMap({
                                    ...graphSelectedMap,
                                    [graph]: {
                                        ...graphSelectedMap[graph],
                                        comment: evt.target.value,
                                    },
                                });
                            }}
                            minRows={5}
                            style={{ width: "100%", padding: "15px" }}
                        />
                        <FormHelperText sx={{ textAlign: "right" }}>
                            {`${graphSelectedMap[graph]?.comment?.length || 0} / 700`}
                        </FormHelperText>
                    </FormControl>
                </DialogContent>
            </GenericDialog>

            <GenericDialog
                title="Desea editar las observaciones?"
                open={confirmEditComment}
                setOpen={setConfirmEditComment}
                handleSave={() => {
                    setGraphSelectedMap({
                        ...graphSelectedMap,
                        [graph]: {
                            ...graphSelectedMap[graph],
                            comment: commentEditActualGraph,
                        },
                    });
                    setConfirmEditComment(false);
                    setOpenDialogActualComment(false);
                }}
                cancelButtonText="No"
                saveButtonText="Si"
                mandatoryText={false}
            ></GenericDialog>

            <GenericDialog
                title="Editar observaciones"
                open={openDialogActualComment}
                setOpen={setOpenDialogActualComment}
                handleSave={() => {
                    if ((graphSelectedMap[graph]?.comment?.length || 0) === 0) {
                        setAlertText("Debe completar la observación!");
                        setSeverity("error");
                        setShowAlert(true);
                        setTimeout(() => {
                            setShowAlert(false);
                            setAlertText("");
                            setSeverity("");
                        }, 2000);
                        return;
                    }
                    setConfirmEditComment(true);
                }}
            >
                <DialogContent>
                    <FormControl>
                        <h3 style={{ margin: 0 }}>Actividad</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.title}
                        </Typography>
                        <h3 style={{ margin: 0 }}>Enunciado</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.statement}
                        </Typography>
                        <h3 style={{ margin: 0 }}>Descripción</h3>
                        <Typography variant="p" sx={{ mb: 2 }}>
                            {studentActivity?.course_activity?.activity?.description}
                        </Typography>
                        <h3>
                            A partir del gráfico analizado, ¿Qué observaciones puede compartir con
                            el docente?*
                        </h3>
                        <TextareaAutosize
                            maxLength={700}
                            defaultValue={graphSelectedMap[graph]?.comment}
                            value={commentEditActualGraph}
                            onChange={(evt) => {
                                setCommentEditActualGraph(evt.target.value);
                            }}
                            minRows={5}
                            style={{ width: "100%", padding: "15px" }}
                        />
                        <FormHelperText sx={{ textAlign: "right" }}>
                            {`${
                                commentEditActualGraph?.length ||
                                graphSelectedMap[graph]?.comment?.length ||
                                0
                            } / 700`}
                        </FormHelperText>
                    </FormControl>
                </DialogContent>
            </GenericDialog>

            {showAlert && (
                <GenericAlert
                    severity={severity}
                    text={alertText}
                    style={{
                        position: "fixed",
                        zIndex: "9999999999999999",
                        top: "50%",
                        left: "42%",
                    }}
                />
            )}
        </div>
    );
};

export default SocialGraphs;
